import { defineComponent as _defineComponent } from 'vue'
import { offset, flip } from '@floating-ui/vue'
import { ref, watch, computed, nextTick } from 'vue'
import { nanoid } from 'nanoid'

import Command from './command/Command.vue'
import CommandInput from './command/CommandInput.vue'
import CommandList from './command/CommandList.vue'
import CommandGroup from './command/CommandGroup.vue'
import CommandItem from './command/CommandItem.vue'
import CommandEmpty from './command/CommandEmpty.vue'
import { useDismiss, usePopover, unwrapElement } from '@/composables/popover'
import { useContext } from '@/composables/context'
import { transportationEmojis, getEmojiTranslations } from '@/utils/emoji'

export interface EmojiPickerProps {
  /** The selected unicode emoji  */
  value?: string
  /** Array of suggested emojis to display */
  suggestedEmojis?: string[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'EmojiPicker',
  props: {
    value: { default: undefined },
    suggestedEmojis: { default: () => [] }
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: string, suggestedEmojis: string[] };





const { i18n } = useContext()

const searchQuery = ref('')

const triggerId = `emoji-picker-trigger-${nanoid()}`

function onSelect(emoji: string) {
  emit('input', emoji)
  isOpen.value = false
}

// Popover Functionality -------------------------------------------------------
const isOpen = ref(false)

watch(isOpen, (newValue) => {
  if (!newValue) {
    searchQuery.value = ''
  }
})

const { referenceRef, floatingRef, floatingStyles } = usePopover({
  placement: 'bottom-start',
  isOpen,
  middleware: [
    offset(4),
    flip({ padding: 16, fallbackPlacements: ['top-start'] }),
  ],
})
useDismiss({ referenceRef, floatingRef, isOpen })

watch(isOpen, (newIsOpen) => {
  if (!newIsOpen) {
    nextTick(() => {
      const referenceElement = unwrapElement(referenceRef.value)
      if (!(referenceElement instanceof HTMLElement)) {
        return
      }

      const triggerElements =
        referenceElement.getElementsByClassName('popover-trigger')
      if (!triggerElements.length) {
        referenceElement.focus()
        return
      }

      const elementToFocus = triggerElements[0]
      if (elementToFocus instanceof HTMLElement) {
        elementToFocus.focus()
      }
    })
  }
})

// Emojis ----------------------------------------------------------------------
const transportationEmojisWithTranslations = computed(() => {
  return transportationEmojis.map((emojiItem) => {
    // used to filter emojis by different languages
    const tags = getEmojiTranslations(emojiItem.emoji, i18n.locale)
    // used to show when hovering over the emoji
    const title = tags.length
      ? tags[0].charAt(0).toLocaleUpperCase() + tags[0].substring(1)
      : emojiItem.name
    return {
      ...emojiItem,
      title,
      tags,
    }
  })
})

const emojiSuggestionsWithTranslations = computed(() => {
  if (!props.suggestedEmojis.length) {
    return []
  }

  return transportationEmojisWithTranslations.value.filter((emojiItem) => {
    return props.suggestedEmojis.includes(emojiItem.emoji)
  })
})

return { __sfc: true,props, emit, i18n, searchQuery, triggerId, onSelect, isOpen, referenceRef, floatingRef, floatingStyles, transportationEmojisWithTranslations, emojiSuggestionsWithTranslations, Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty }
}

})